import {
  WSAlert,
  WSButton,
  WSCard,
  WSDataItem,
  WSEmptyState,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  BulkStatus,
  IBulkPayableBatch
} from "@wingspanhq/payments/dist/interfaces";
import { getUploadedFilename } from "../../utils/getUploadedFilename";
import { BulkResource } from "../../types";

import {
  isBatchFullyImported,
  isBatchPartiallyImported,
  isFileUploaded,
  isUploadFinished,
  isUploadInProgress
} from "../../utils/bulkBatchUtils";
import { BulkBatchUploadProgress } from "../../components/BulkBatchUploadProgress";
import { BulkBatchImportProgress } from "../../components/BulkBatchImportProgress";
import { useHistory } from "react-router-dom";
import pluralize from "pluralize";
import { IPayableBatchImportSummary } from "../../services/types";
import { useModalPayablesColumnsGuide } from "../../components/ModalPayableColumnsGuide";
import { useFailedBatchItems } from "../../hooks/useFailedBatchItems";
import { useBulkBatchStatus } from "../../hooks/useBulkBatchStatus";
import { useBulkBatchStatusActions } from "../../hooks/useBulkBatchActions";
import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";

type Props = {
  bulkBatch: IBulkPayableBatch;
  bulkBatchSummary: IPayableBatchImportSummary;
  downloadExampleCSV: () => void;
  downloadErrorsCSV: () => void;
};

export const PayableBatchStatusWidget: React.FC<Props> = ({
  bulkBatch,
  bulkBatchSummary,
  downloadExampleCSV,
  downloadErrorsCSV
}) => {
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();
  const payablesColumnsGuideModal = useModalPayablesColumnsGuide();
  const { failedBatchItems, queryAllFailedBulkBatchItems } =
    useFailedBatchItems(bulkBatch.bulkPayableBatchId, BulkResource.Payable);
  const isBatchProcessing = [
    BulkStatus.Pending,
    BulkStatus.Processing
  ].includes(bulkBatch.status);
  const isEmptyBatch = bulkBatchSummary.listSize === 0;

  const renderStats = (showSeeAll: boolean = true) => {
    return (
      <WSList gap="XL">
        <WSList gap="S">
          <WSFlexBox.CenterY justify="space-between">
            <WSText.Paragraph weight="medium" color="gray600">
              Payables
            </WSText.Paragraph>
            {showSeeAll && (
              <WSButton.Link
                rightIcon="chevron-right"
                onClick={() => {
                  history.push(
                    `/member/invoices/payables/all?bulkBatchId=${bulkBatch.bulkPayableBatchId}`
                  );
                }}
              >
                See all
              </WSButton.Link>
            )}
          </WSFlexBox.CenterY>
          <WSDataItem
            inline
            label={{
              text: "Line items count"
            }}
            value={bulkBatchSummary.payablesCount}
          />
          <WSDataItem
            inline
            label="Total payables"
            value={bulkBatchSummary.totalPayablesAmount}
            formatterOptions={{
              currency: defaultCurrency
            }}
          />
        </WSList>

        <WSList gap="S">
          <WSFlexBox.CenterY justify="space-between">
            <WSText.Paragraph weight="medium" color="gray600">
              Deductions
            </WSText.Paragraph>
            {showSeeAll && (
              <WSButton.Link
                rightIcon="chevron-right"
                onClick={() => {
                  history.push(
                    `/member/invoices/payables/deductions?bulkBatchId=${bulkBatch.bulkPayableBatchId}`
                  );
                }}
              >
                See all
              </WSButton.Link>
            )}
          </WSFlexBox.CenterY>
          <WSDataItem
            inline
            label={{
              text: "Deductions created"
            }}
            value={bulkBatchSummary.deductionsCreatedCount}
          />
          <WSDataItem
            inline
            label="Total deductions"
            value={bulkBatchSummary.totalDeductionsAmount}
            formatterOptions={{
              currency: defaultCurrency
            }}
          />
        </WSList>

        <WSList gap="S">
          <WSFlexBox.CenterY justify="space-between">
            <WSText.Paragraph weight="medium" color="gray600">
              Import total
            </WSText.Paragraph>
            <WSText.Paragraph weight="medium" color="gray700">
              {formatMoney(bulkBatchSummary.netImportAmount, defaultCurrency)}
            </WSText.Paragraph>
          </WSFlexBox.CenterY>
          <WSDataItem
            inline
            label={{
              text: "Total items imported"
            }}
            value={bulkBatchSummary.totalItemsImported}
          />
        </WSList>
      </WSList>
    );
  };

  const renderBatchStates = () => {
    // Completed state UI
    if (isBatchFullyImported(bulkBatch)) {
      return (
        <WSList gap="XL">
          <BulkBatchImportProgress
            batchId={bulkBatch.bulkPayableBatchId}
            bulkResource={BulkResource.Payable}
          />
          {renderStats()}
        </WSList>
      );
    }
    // Partial imported state UI
    if (isBatchPartiallyImported(bulkBatch)) {
      return (
        <WSList gap="L">
          <BulkBatchImportProgress
            batchId={bulkBatch.bulkPayableBatchId}
            bulkResource={BulkResource.Payable}
          />
          <WSAlert icon="alert" theme="warning" title="Errors found">
            <ul>
              {failedBatchItems.map(failedItem => (
                <li>
                  {pluralize("item", failedItem.value, true)}:{" "}
                  {failedItem.label}
                </li>
              ))}
            </ul>
          </WSAlert>
          {renderStats()}
        </WSList>
      );
    }
    // Failed state UI
    if (bulkBatch.status === BulkStatus.Failed) {
      return (
        <WSList gap="L">
          <BulkBatchImportProgress
            batchId={bulkBatch.bulkPayableBatchId}
            bulkResource={BulkResource.Payable}
          />
          <WSAlert icon="alert" theme="warning" title="Errors found">
            <ul>
              {failedBatchItems.map(failedItem => (
                <li>
                  {pluralize("item", failedItem.value, true)}:{" "}
                  {failedItem.label}
                </li>
              ))}
            </ul>
          </WSAlert>
          {renderStats(false)}
        </WSList>
      );
    }

    // Importing state UI
    if (isBatchProcessing) {
      return (
        <WSList gap="L">
          <BulkBatchImportProgress
            batchId={bulkBatch.bulkPayableBatchId}
            bulkResource={BulkResource.Payable}
          />
          {renderStats(false)}
        </WSList>
      );
    }

    // Awaiting import state UI
    if (isUploadFinished(bulkBatch, isEmptyBatch)) {
      return (
        <WSList gap="L">
          <WSFlexBox.CenterY>
            <WSIcon name="details" mr="XS" />
            <WSText.ParagraphSm weight="book" color="gray600">
              A spreadsheet is uploaded and ready to be imported and processed
              as contractors.
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>

          <WSAlert icon="bulb" size="M" theme="info" title="Pro tip">
            View upload to check that all information is accurate prior to
            completing the import process. If there are any changes to make to
            the spreadsheet, delete this import and start a new batch after
            you’ve updated your spreadsheet.
          </WSAlert>

          {renderStats(false)}
        </WSList>
      );
    }

    // Uploading state UI
    if (isUploadInProgress(bulkBatch)) {
      return (
        <WSList gap="L">
          <BulkBatchUploadProgress
            bulkBatchId={bulkBatch.bulkPayableBatchId}
            bulkResource={BulkResource.Payable}
          />
          <WSFlexBox wrap="nowrap">
            <WSIcon size="S" name="time" mr="XS" />
            <WSText.ParagraphSm weight="book" color="gray600">
              A spreadsheet is being uploaded. If you close this page, the
              progress will continue. Ensure you complete the last step to
              import and process the spreadsheet once it has been uploaded.
            </WSText.ParagraphSm>
          </WSFlexBox>
        </WSList>
      );
    }

    // File is selected but not uploaded yet
    if (isEmptyBatch && isFileUploaded(bulkBatch)) {
      return (
        <WSFlexBox wrap="nowrap">
          <WSIcon name="details" mr="XS" />
          <WSText.ParagraphSm weight="book" color="gray600">
            A spreadsheet is selected but not uploaded yet. Click the view
            upload button to upload your formatted spreadsheet.
          </WSText.ParagraphSm>
        </WSFlexBox>
      );
    }

    // Show empty state if there are no batch items
    if (isEmptyBatch && !isFileUploaded(bulkBatch)) {
      return (
        <WSEmptyState
          hasBorder={false}
          orientation="horizontal"
          size="M"
          type="documents"
          title="File not uploaded"
          description={
            <WSText.ParagraphSm weight="book" color="gray600" inline>
              Upload your formatted spreadsheet to the batch. Need help setting
              up your spreadsheet?
              <WSButton.Link onClick={() => payablesColumnsGuideModal.open()}>
                Learn more here.
              </WSButton.Link>
            </WSText.ParagraphSm>
          }
        />
      );
    }
  };

  const { pill } = useBulkBatchStatus(
    bulkBatch.bulkPayableBatchId,
    BulkResource.Payable
  );

  const { menuItems, buttons } = useBulkBatchStatusActions({
    bulkBatchId: bulkBatch.bulkPayableBatchId,
    bulkResource: BulkResource.Payable,
    isEmptyBatch,
    downloadExampleCSV,
    downloadErrorsCSV,
    hideButtonOn: false
  });

  return (
    <WSCard
      header={{
        label:
          getUploadedFilename(bulkBatch, BulkResource.Payable) ||
          "Upload spreadsheet",
        value: {
          pill
        }
      }}
      actions={{
        orientation: { xs: "vertical", s: "horizontal" },
        alignment: "fill",
        menuItems,
        buttons
      }}
      divider
    >
      {renderBatchStates()}
    </WSCard>
  );
};
